import { API, PoundsPerCubicFoot } from "@/libs/units/density";
import type { GasProperties } from "@/libs/calculations/fluidProperties/d";
import { withoutAbsentGasComponents } from "@/libs/calculations/gasPhase/gasTable";

// Ovalle et al. gas-condensate-ratio correlation (PVT!O61), ported
// cell-for-cell from static/PVT_and_Flow_Correlations_Reference.xlsx
// (sheet "PVT", cell O61 = 10^6/BJ48, coefficient table BJ38:BM42).
// Unlike the Peng-Robinson GCR (PVT!O60, see pengRobinson.ts), this is not
// gated behind a correlation dropdown — the workbook always evaluates it.

// PVT!BJ38:BM42 — pressure, condensate API, gas SG, and temperature terms.
// Each column is summed into PVT!BJ45 "SumTr"; note this is not ln(Rv) yet.
const OVALLE_PRESSURE_COEFFICIENTS = [20.809, -6.7095, 0.5136];
const OVALLE_API_COEFFICIENTS = [
  11.175, -1.2965, 0.042311, -0.0005438, 0.0000024889
];
const OVALLE_GAS_SG_COEFFICIENTS = [-13.365, 27.652, -18.598, 4.3658, 0];
const OVALLE_TEMPERATURE_COEFFICIENTS = [-1.5309, 0.0058453, 0.0000014, 0, 0];

// PVT!O61 = 10^6/BJ48. condensateApiOverride is PVT!D22, the raw user override
// input; callers only run this when it is > 0.
export function getOvalleGasCondensateRatioScfStb(
  bottomOfTubingPressurePsia: number,
  gasSpecificGravity: number,
  temperatureF: number,
  condensateApiOverride: number
): number {
  if (
    !Number.isFinite(bottomOfTubingPressurePsia) ||
    bottomOfTubingPressurePsia <= 0
  ) {
    return 0;
  }

  const bj43 =
    OVALLE_PRESSURE_COEFFICIENTS[0] +
    OVALLE_PRESSURE_COEFFICIENTS[1] * Math.log(bottomOfTubingPressurePsia) +
    OVALLE_PRESSURE_COEFFICIENTS[2] *
      Math.pow(Math.log(bottomOfTubingPressurePsia), 2);
  const bk43 =
    OVALLE_API_COEFFICIENTS[0] +
    OVALLE_API_COEFFICIENTS[1] * condensateApiOverride +
    OVALLE_API_COEFFICIENTS[2] * Math.pow(condensateApiOverride, 2) +
    OVALLE_API_COEFFICIENTS[3] * Math.pow(condensateApiOverride, 3) +
    OVALLE_API_COEFFICIENTS[4] * Math.pow(condensateApiOverride, 4);
  const bl43 =
    OVALLE_GAS_SG_COEFFICIENTS[0] +
    OVALLE_GAS_SG_COEFFICIENTS[1] * gasSpecificGravity +
    OVALLE_GAS_SG_COEFFICIENTS[2] * Math.pow(gasSpecificGravity, 2) +
    OVALLE_GAS_SG_COEFFICIENTS[3] * Math.pow(gasSpecificGravity, 3) +
    OVALLE_GAS_SG_COEFFICIENTS[4] * Math.pow(gasSpecificGravity, 4);
  const bm43 =
    OVALLE_TEMPERATURE_COEFFICIENTS[0] +
    OVALLE_TEMPERATURE_COEFFICIENTS[1] * temperatureF +
    OVALLE_TEMPERATURE_COEFFICIENTS[2] * Math.pow(temperatureF, 2) +
    OVALLE_TEMPERATURE_COEFFICIENTS[3] * Math.pow(temperatureF, 3) +
    OVALLE_TEMPERATURE_COEFFICIENTS[4] * Math.pow(temperatureF, 4);
  // PVT!BJ45 = BJ43+BK43+BL43+BM43
  const sumTr = bj43 + bk43 + bl43 + bm43;

  // PVT!BJ46, then BJ48 = EXP(BJ46)
  const lnRv = 3.684 + 0.61967 * sumTr + 0.015359 * Math.pow(sumTr, 2);

  const rvStbPerMMscf = Math.exp(lnRv);

  return 1000 / rvStbPerMMscf;
}

// PVT!AW25 = IF(D22>0, 141.5/(131.5+D22), SUM(AQ78:AQ91)/62.428). An entered
// Condensate API (PVT!D22) wins; otherwise the composition's stock tank
// density, AQ78 being AE95*M78 — each component's liquid-phase share times its
// stock tank density, which is gasTable's condensateSTp. Like Ovalle above,
// the workbook does not gate this on the compressibility correlation.
export function getcondensateSpecificGravity(
  gasProperties: GasProperties[],
  condensateApi: number | null | undefined
): number {
  if (condensateApi && condensateApi > 0) {
    return API.toStandardUnit(condensateApi);
  }

  return PoundsPerCubicFoot.toStandardUnit(
    withoutAbsentGasComponents(gasProperties).reduce(
      (sum, g) => sum + g.condensateSTp,
      0
    )
  );
}

// PVT!O64 = IF(D22>0, D22, 141.5/AW25-131.5).
export function getCondensateApi(
  condensateApi: number | null | undefined,
  condensateSpecificGravity: number
): number | null {
  if (condensateApi && condensateApi > 0) {
    return condensateApi;
  }

  if (
    !Number.isFinite(condensateSpecificGravity) ||
    condensateSpecificGravity <= 0
  ) {
    return null;
  }
  return API.toThisUnit(condensateSpecificGravity);
}

export function getTotalInSituGasRateRcfD(
  gasProperties: GasProperties[],
  hasGasCompositeValues: boolean,
  gasFlowRateSCFD: number,
  gasVolumeFactorCFSFCF: number
): number {
  if (hasGasCompositeValues) {
    return withoutAbsentGasComponents(gasProperties).reduce(
      (sum, component) => sum + (component.inSituRateCfd || 0),
      0
    );
  }
  return gasFlowRateSCFD * gasVolumeFactorCFSFCF || 0;
}

export function getSaturatedGasRateRcfD(
  gasLiquidRatioScfStb: number,
  rsoCorrectedScfStb: number,
  rswCorrectedScfStb: number,
  gasCondensateRatioScfStb: number,
  oilFlowRateSTBD: number,
  waterFlowRateSTBD: number,
  gasVolumeFactorCFSFCF: number,
  totalInSituGasRateRcfD: number,
  condensateRateRcfD: number
): number {
  if (
    gasLiquidRatioScfStb -
      rsoCorrectedScfStb -
      rswCorrectedScfStb -
      gasCondensateRatioScfStb <=
    0
  ) {
    return Math.max(0, totalInSituGasRateRcfD - condensateRateRcfD);
  }
  return Math.max(
    0,
    (rsoCorrectedScfStb + rswCorrectedScfStb) *
      (oilFlowRateSTBD + waterFlowRateSTBD) *
      gasVolumeFactorCFSFCF || 0
  );
}
